<template>
    <div>
        <card class="cursor-pointer mb-2">
            <div class="p-3 text-center fareharbor-card"
                 :class="{'active' : integrated}"
                 @click="open()">
                <img src="/img/fareharbor-logo.png" style="max-height: 60px"/>
            </div>
        </card>
        <modal ref="modal"
               size="lg"
               @hide="close()"
               :scrollable="true"
               :has-footer="false">
            <template slot="title">FareHarbor Integration</template>
            <card-spinner
                v-if="loading"
                style="height:200px"
            />
            <transition name="fade-in-down">
                <div v-if="integrated">
                    <div v-if="!editMode">
                        <div v-if="!logsMode">
                            <div class="alert alert-success">
                                <div>
                                    <span> Integrated On FareHarbor Shortname: {{ fareHarborAccountId }} </span>
                                    <button-loading type="button" class="btn btn-danger ml-2 float-right"
                                                    :loading="form.submitting"
                                                    @click="removeIntegration">
                                        Remove All Integrations
                                    </button-loading>
                                </div>
                            </div>
                            <div class="mb-4 text-right">
                                <button type="button" class="btn btn-primary" @click="enterEditMode(null)">
                                    New connection
                                </button>
                            </div>
                            <div style="max-height: calc(100vh - 220px);overflow-y: auto;overflow-x: hidden;">
                                <transition-group name="slide-right" tag="p" @leave="transitioningDone()">
                                    <div class="card card-default card-default mb-2"
                                         v-for="connection in fareHarborConnections"
                                         :key="connection.id">
                                        <div class="card-header d-flex pb-1">
                                            <p class="text-dark">
                                                <small class="text-muted">
                                                    Campaign: {{ connection.campaign_name }}
                                                </small>
                                            </p>
                                            <div class="ml-auto">
                                                <button title="Logs"
                                                        type="button"
                                                        class="btn btn-default btn-sm btn-icon ml-1"
                                                        @click="loadLogs(connection)">
                                                    <i class="fa fa-fw fa-history"></i>
                                                </button>
                                                <button title="Edit"
                                                        type="button"
                                                        class="btn btn-default btn-sm btn-icon ml-1"
                                                        @click="enterEditMode(connection)">
                                                    <i class="fa fa-fw fa-pencil-alt"></i>
                                                </button>
                                                <button title="Delete"
                                                        type="button"
                                                        class="btn btn-default btn-sm btn-icon ml-1"
                                                        @click="(evt) => {remove(connection, evt)}">
                                                    <i class="fa fa-fw fa-trash"></i>
                                                </button>
                                            </div>
                                        </div>
                                        <div class="card-body p-1">
                                            <div class="text-right pr-2">
                                                <small class="font-italic text-muted">
                                                    Last Synced at:
                                                    {{ connection.last_synced_at || 'not synced'}}
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                </transition-group>
                            </div>
                            <transition name="fade">
                                <div v-if="empty" class="card card-default m-0 p-3 empty-placeholder"
                                     @click="enterEditMode(null)">
                                    <div class="card-body text-center" title="Add new connection">
                                        <i class="fa fa-plus" style="font-size: 1.285rem;"></i>
                                        <div>Add your first connection</div>
                                    </div>
                                </div>
                            </transition>
                        </div>
                        <div v-if="logsMode">
                            <card-spinner v-if="loadingLogs"></card-spinner>
                            <div v-else>
                                <div style="max-height: calc(100vh - 300px); overflow: auto;">
                                    <table class="table table-hover table-striped table-light table-sm mb-0">
                                        <thead>
                                        <tr>
                                            <th class="pl-3">Result</th>
                                            <th>Customer Name</th>
                                            <th>Booking Status</th>
                                            <th>Tour ID</th>
                                            <th>Booking End Date</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        <template v-for="(log,index) in logs.data">
                                            <tr>
                                                <td>
                                              <span v-if="log.result_status !== 'success'"
                                                    class="badge px-2 py-1 text-uppercase"
                                                    data-toggle="collapse"
                                                    :data-target="`#accordion${index}`"
                                                    :class="statusClassOf(log)"
                                                    v-tooltip="tooltipFor(log)">
                                                  {{ log.result_status }}
                                                  <i class="fa fa-chevron-down"></i>
                                              </span>
                                                    <span v-else class="badge px-2 py-1 text-uppercase"
                                                          :class="statusClassOf(log)"
                                                          v-tooltip="tooltipFor(log)">
                                                  {{ log.result_status }}
                                              </span>
                                                </td>
                                                <td>{{log.customer_name}}</td>
                                                <td>{{log.booking_status}}</td>
                                                <td>{{log.tour_id}}</td>
                                                <td>{{log.end_date}}</td>
                                            </tr>
                                            <tr>
                                                <td colspan="8" class="p-0">
                                                    <div :id="`accordion${index}`" class="collapse p-1">
                                                        <pre><code>{{ log.errors }}</code></pre>
                                                    </div>
                                                </td>
                                            </tr>
                                        </template>
                                        </tbody>
                                    </table>
                                </div>
                                <pagination class="mb-0 mt-3" :meta="logs.meta"
                                            :links="logs.links"
                                            @page="fetchAppointmentLogs"></pagination>
                                <div class="text-right">
                                    <button @click="closeLogs()" class="btn btn-default mt-2">Back</button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="editMode">
                        <div class="row">
                            <div class="col-md-12">
                                <div class="form-group" :class="{'is-invalid': form.errors.has('campaign_id')}">
                                    <label>Campaign</label>
                                    <select class="form-control" v-model="form.campaign_id">
                                        <option :value="null"></option>
                                        <option v-for="campaign in campaigns"
                                                :key="campaign.id" :value="campaign.id">
                                            {{ campaign.name }}
                                        </option>
                                    </select>
                                    <p v-if="form.errors.has('campaign_id')" class="invalid-feedback">
                                        {{ form.errors.get('campaign_id') }}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div class="row">
                            <div class="col-md-6">
                                <div class="form-group"
                                     :class="{'is-invalid': form.errors.has('sync_at')}">
                                    <div class="d-flex justify-between">
                                        <label>Sync at</label>
                                        <small class="ml-auto float-right text-muted" style="padding-top: 4px">
                                            Server Time:
                                            <server-time
                                                class="ml-1"
                                                format="hh:mm:ss a"
                                            />
                                        </small>
                                    </div>

                                    <vue-ctk-date-time-picker
                                        v-model="form.sync_at"
                                        label="Select time"
                                        :no-label="true"
                                        format="hh:mm a"
                                        formatted="hh:mm a"
                                        color="#0374D7"
                                        :minute-interval="1"
                                        :only-time="true"
                                    />
                                    <p v-if="form.errors.has('sync_at')" class="invalid-feedback">
                                        {{ form.errors.get('sync_at') }}
                                    </p>
                                </div>
                            </div>
                            <div class="col-md-6">
                                <div class="form-group"
                                     :class="{'is-invalid': form.errors.has('send_at')}">
                                    <label>Send invites at</label>
                                    <vue-ctk-date-time-picker
                                        v-model="form.send_at"
                                        label="Select time"
                                        :no-label="true"
                                        color="#0374D7"
                                        format="hh:mm a"
                                        formatted="hh:mm a"
                                        :without-header="true"
                                        :minute-interval="15"
                                        :only-time="true"
                                    />
                                    <p v-if="form.errors.has('send_at')" class="invalid-feedback">
                                        {{ form.errors.get('send_at') }}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <hr>
                        <legend class="font-weight-bold mb-2">
                            Tour IDs
                        </legend>
                        <div class="row">
                            <div class="col-md-9">
                                <div class="form-group">
                                    <input class="form-control" type="number" v-model="tourId" min="0">
                                </div>
                            </div>
                            <div class="col-md-3">
                                <button @click.prevent="addTourId" class="btn btn-primary"><i class="fa fa-plus"></i> Add Tour ID </button>
                            </div>
                        </div>
                        <div v-if="form.tour_ids && form.tour_ids.length" class="row m-2">
                            <div v-for="(tour_id, index) in form.tour_ids">
                                <span class="badge badge-pill" :class="[tourId === tour_id ? 'badge-warning' : 'badge-primary']">
                                    {{ tour_id }}&nbsp
                                    <i class="fas fa-times cursor-pointer text-white" @click.prevent="removeTourId(index)"></i>
                                </span>
                            </div>
                        </div>
                        <div v-else>
                            <div class="alert alert-warning" role="alert" >
                                If no Tour Ids are selected all incoming tours will be processed.
                            </div>
                        </div>
                        <legend class="font-weight-bold mb-2">
                            Booking Statuses
                        </legend>
                        <div :class="{'is-invalid': form.errors.has('booking_statuses')}">
                            <p v-if="form.errors.has('booking_statuses')" class="invalid-feedback">
                                {{ form.errors.get('booking_statuses') }}
                            </p>
                        </div>
                        <div class="mb-3 d-flex align-items-center">
                            <div class="ml-3 custom-control custom-checkbox"
                                 v-if="bookingStatuses.length">
                                <input type="checkbox"
                                       class="custom-control-input"
                                       id="bookingStatusesAll"
                                       v-model="bookingStatusesAll"
                                       @change="selectAllBookingStatuses">
                                <label class="custom-control-label" for="bookingStatusesAll">
                                    Select all
                                </label>
                            </div>
                        </div>
                        <div class="row" v-if="bookingStatuses.length && !loadBookingStatuses">
                            <div class="col-md-4 checkbox"
                                 v-for="status in bookingStatuses" :key="status">
                                <label class="checkbox-inline px-2">
                                    <input v-model="form.booking_statuses" type="checkbox"
                                           :value="status" @change="selectedAllBookingStatuses">
                                    {{ status }}
                                </label>
                            </div>
                        </div>
                    </div> <!-- End of EditMode -->
                </div>
            </transition>
            <div v-if="!integrated && !editMode">
                <div v-if="!integrated">
                    <input-text label="FareHarbor Shortname" v-model="form.fareharbor_account_id" field="fareHarborAccountId"></input-text>
                    <p class="text-danger" v-if="integrationError">Fareharbor Shortname Field Required For Integration!</p>
                </div>
                <div class="modal-footer mt-2 pr-0 pb-0 pl-0">
                    <button class="btn btn-default"
                            type="button"
                            data-dismiss="modal"
                            @click="close()">
                        Close
                    </button>
                    <button-loading type="button" class="btn btn-primary ml-2"
                                    @click="initialIntegration">
                        Integrate
                    </button-loading>
                </div>
            </div>
            <div v-if="editMode">
                <div class="modal-footer mt-2 pr-0 pb-0 pl-0">
                    <button class="btn btn-default"
                            type="button"
                            data-dismiss="modal"
                            @click="exitEdit()">
                        Close
                    </button>
                    <button-loading type="button" class="btn btn-primary ml-2"
                                    :loading="form.submitting"
                                    @click="saveIntegration">
                        Save
                    </button-loading>
                </div>
            </div>
        </modal>
    </div>
</template>
<script>
    import axios from 'axios';
    import isEqual from 'lodash';
    import VueCtkDateTimePicker from 'vue-ctk-date-time-picker';
    import 'vue-ctk-date-time-picker/dist/vue-ctk-date-time-picker.css';

    export default {
        props: {
            location: {required: true}
        },
        components: {
            VueCtkDateTimePicker
        },
        data : () => ({
            loading: true,
            campaigns: [],
            editMode: false,
            logsMode: false,
            form: new Form({}),
            tourId: '',
            loadBookingStatuses: false,
            bookingStatuses: ['booked', 'canceled', 'rebooked'],
            bookingStatusesAll: false,
            fareHarborAccountId: null,
            selectedConnection: null,
            transitioning: false,
            fareHarborConnections: [],
            integrationError: false,
            loadingLogs: false,
            logs: [],
            query: null,
        }),
        watch: {
            location: {
                immediate: true,
                handler() {
                    this.fareHarborAccountId = this.location.fareharbor_account_id;
                    this.form = new Form({
                        location_id: this.location.id,
                        fareharbor_account_id: this.location.fareharbor_account_id,
                    });
                }
            }
        },
        computed: {
            integrated() {
                let is_integrated = !!this.fareHarborAccountId;
                return is_integrated;
            },

            empty() {
                return !this.fareHarborConnections.length && !this.transitioning;
            },
        },
        methods: {
            async open() {
                this.$refs.modal.open();
                this.$emit('show', true);
                if(this.integrated){
                    this.getFareharborConnections();
                }
                this.campaigns = this.location.campaigns;
                this.loading = false;
            },

            close() {
                this.$emit('hide');
                this.$emit('save-integration', this.location.id);
                this.logsMode           = false;
                this.selectedConnection = null;
                this.exitEdit();
            },

            exitEdit() {
                this.editMode = false;
                this.form     = new Form({});
            },

            async getFareharborConnections(){
                await this.$http.get(this.$laroute.route('fareharbor-getIntegrations', {
                    location: this.location.id
                }))
                    .then(({data}) => {
                        this.fareHarborConnections = data.data;
                    }).catch(error => console.error(error));
                this.loading = false;
            },
            initialIntegration(){
                if(this.form.fareharbor_account_id === null || this.form.fareharbor_account_id.match(/^ *$/) !== null){
                    this.integrationError = true;
                    return;
                }
                this.integrationError = false;
                this.fareHarborAccountId = this.form.fareharbor_account_id;
                this.enterEditMode();
            },
            enterEditMode(connection){
                this.loading = true;
                this.fareHarborAccountId = this.form.fareharbor_account_id;
                this.form = new Form({
                    id: connection ? connection.id : null,
                    campaign_id : connection ? connection.campaign_id : null,
                    sync_at : connection ? connection.sync_at : '12:00 am',
                    send_at : connection ? connection.send_at : '12:00 am',
                    location_id: this.location.id,
                    tour_ids: connection ? connection.tour_ids : [],
                    booking_statuses: connection ? connection.booking_statuses : [],
                    fareharbor_account_id: this.fareHarborAccountId,
                });
                if(this.form.booking_statuses.length === 3){
                    this.bookingStatusesAll = true;
                }else{
                    this.bookingStatusesAll = false;
                }
                this.loading = false;
                this.editMode = true;
            },

            addTourId(){
                if(this.tourId === null || this.tourId.match(/^ *$/) !== null){
                    return;
                }
                if (!this.form.tour_ids.includes(this.tourId) && this.tourId > 0){
                    this.form.tour_ids.push(this.tourId);
                    this.tourId = '';
                }
            },

            removeTourId(tour_index){
                this.form.tour_ids.splice(tour_index, 1);
            },

            selectAllBookingStatuses() {
                if (this.bookingStatusesAll) {
                    this.form.booking_statuses = this.bookingStatuses;
                    return;
                }
                this.form.booking_statuses = [];
            },

            selectedAllBookingStatuses() {
                if (_.isEqual(_.sortBy(this.bookingStatuses), _.sortBy(this.form.booking_statuses))) {
                    this.bookingStatusesAll = true;
                    return;
                }
                this.bookingStatusesAll = false;
            },

            async saveIntegration(){
                this.loading = true;
                try{
                    if(this.form.id){
                        this.form.put(this.$laroute.route('fareharbor-updateIntegration'))
                            .then(() => {
                                this.getFareharborConnections();
                                swal.fire({
                                    title: 'Saved',
                                    type: 'success',
                                });
                                this.editMode = false;
                            });
                    }else{
                        this.form.post(this.$laroute.route('fareharbor-createIntegration'))
                            .then(() => {
                                this.getFareharborConnections();
                                swal.fire({
                                    title: 'Saved',
                                    type: 'success',
                                });
                                this.editMode = false;
                            });
                    }

                } catch (e) {
                    console.error(e);
                } finally {
                    this.loading = false;
                }
            },
            async remove(connection, evt) {
                evt.target.parentElement.blur();
                const result = await this.$confirmDialog();
                if (!result) return;
                try {
                    await this.$http.delete(this.$laroute.route('fareharbor-removeIntegration', {
                        fareharborIntegration: connection.id
                    }));
                    const index = this.fareHarborConnections.findIndex(c => c.id === connection.id);
                    this.transitioning = true;
                    this.fareHarborConnections.splice(index, 1);
                    swal.close();
                } catch (e) {
                    console.error('Unable to delete FareHarbor connection!', e);
                    if (!e.response) {
                        swal.fire("Operation failed!", e.message, "error");
                        return;
                    }

                    const response = e.response;
                    if (response.status === 500) {
                        swal.fire("Operation failed!",
                            'Sorry, an unexpected condition was encountered. Please try again later. ' +
                            'If the problem persists, please contact customer support.',
                            "error");
                        return;
                    }

                    swal.fire("Operation failed!", response.data.message, "error");
                }
            },
            async removeIntegration(){
                const result = await this.$confirmDialog();
                if (!result) return;
                try {
                    await this.$http.delete(this.$laroute.route('fareharbor-removeAllIntegrations', {
                        location_id: this.location.id
                    }));
                    swal.close();
                    this.fareHarborAccountId = null;
                    this.form.fareharbor_account_id = null;
                } catch (e) {
                    console.error('Unable to delete FareHarbor connection!', e);
                    if (!e.response) {
                        swal.fire("Operation failed!", e.message, "error");
                        return;
                    }

                    const response = e.response;
                    if (response.status === 500) {
                        swal.fire("Operation failed!",
                            'Sorry, an unexpected condition was encountered. Please try again later. ' +
                            'If the problem persists, please contact customer support.',
                            "error");
                        return;
                    }

                    swal.fire("Operation failed!", response.data.message, "error");
                }
            },

            async transitioningDone() {
                setTimeout(() => {
                    this.transitioning = false
                }, 500);
            },
            async loadLogs(connection){
                this.selectedConnection = connection;
                this.logsMode = true;
                await this.fetchAppointmentLogs();
            },
            async fetchAppointmentLogs(page = 1) {
                try {
                    this.loadingLogs = true;
                    const params     = {page, search: this.query};
                    const {data: logs} = await this.$http.get(`/fareharbor/${this.selectedConnection.id}/getAppointmentLogs`, {params});
                    this.logs = logs;
                } catch (e) {
                    console.error('Unable to load logs: ', e);
                } finally {
                    this.loadingLogs = false;
                }
            },

            async closeLogs() {
                this.logsMode = false;
            },
            statusClassOf(booking) {
                return {
                    'badge-success': booking.result_status === 'success',
                    'has-more badge-danger cursor-pointer': booking.result_status === 'failed',
                    'has-more badge-secondary cursor-pointer': booking.result_status === 'filter-off',
                    'has-more badge-danger cursor-pointer': booking.result_status === 'patient-not-found',
                };
            },

            tooltipFor(booking) {
                const errors = booking.errors;
                return (booking.errors && booking.errors.length) ?
                    {title: errors && errors.length && errors[0]} :
                    null;
            },
        }

    }
</script>
<style lang="scss" scoped>
    legend {
        font-size: 1rem;
    }

    .table th, .table td {
        white-space: nowrap;
        font-size: small;
    }

    .fareharbor-card {
        min-height: 100px;
        filter: grayscale(100%);
        background-repeat: no-repeat;
        background-size: contain;
        opacity: 0.6;
        background-position-y: center;
        background-position-x: center;
        &:hover {
            filter: grayscale(0%);
            opacity: 0.8;
        }
        &.active {
            filter: grayscale(0%);
            opacity: 1;
        }
    }

    .is-invalid {
        .invalid-feedback {
            display: block;
        }
    }

</style>

<style lang="scss">
    .date-time-picker {
        .field-input {
            color: #6e7d87;
            font-weight: 600;
            font-size: 16px;
            font-family: "open sans", Whitney, Lato, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol";
            border: 1px solid #b0b9bf !important;

            &:focus {
                border-color: #5db2fd;
                box-shadow: 0 0 0 0.2rem rgba(3, 116, 215, .25);
            }
        }
    }

    .alert span {
        vertical-align: middle;
        line-height:normal;
    }

    .alert > div {
        line-height: 34px;
    }
</style>
